import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  getDocs,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { firestore } from '../utils/firebaseSetUp';
import { Return } from '../models/return';
import { isSelfHosted } from '../config/infra';
import { authenticatedHttpClient } from './httpClient';
import {
  createApiListCursor,
  getApiListOffset,
} from '../utils/apiListCursor';
import { createPollingSubscription } from '../utils/pollingSubscription';

export type { Return };
export interface ReturnsQueryResult {
  returns: Return[];
  lastDoc: QueryDocumentSnapshot | null;
  hasMore: boolean;
}

async function fetchReturnsPageSelfHosted(
  organizationId: string,
  searchInput: string | undefined,
  lastReturn: DocumentSnapshot | undefined,
  limitCount: number
): Promise<ReturnsQueryResult> {
  const offset = getApiListOffset(lastReturn, 0);
  const params = new URLSearchParams();
  params.set('limit', String(limitCount));
  params.set('offset', String(offset));
  if (searchInput?.trim()) {
    params.set('search_term', searchInput.trim().toLowerCase());
  }
  const result = await authenticatedHttpClient.get<ReturnsQueryResult>(
    `/returns/${organizationId}?${params.toString()}`
  );
  const hasMore = result.hasMore ?? result.returns.length >= limitCount;
  return {
    returns: result.returns,
    hasMore,
    lastDoc: hasMore
      ? (createApiListCursor(offset + limitCount) as unknown as QueryDocumentSnapshot)
      : null,
  };
}

/**
 * Load soft-deleted returns via the server API (trash bin).
 */
export const loadDeletedReturns = async (
  organizationId: string,
  searchInput?: string,
  lastReturn?: DocumentSnapshot,
  limitCount: number = 10
): Promise<ReturnsQueryResult> => {
  const offset = getApiListOffset(lastReturn, 0);
  const params = new URLSearchParams();
  params.set('limit', String(limitCount));
  params.set('offset', String(offset));
  params.set('deleted_only', 'true');
  if (searchInput?.trim()) {
    params.set('search_term', searchInput.trim().toLowerCase());
  }
  const result = await authenticatedHttpClient.get<ReturnsQueryResult>(
    `/returns/${organizationId}?${params.toString()}`
  );
  const hasMore = result.hasMore ?? result.returns.length >= limitCount;
  return {
    returns: result.returns,
    hasMore,
    lastDoc: hasMore
      ? (createApiListCursor(offset + limitCount) as unknown as QueryDocumentSnapshot)
      : null,
  };
};

/**
 * Load returns with pagination and optional search.
 */
export const loadReturns = async (
  organizationId: string,
  searchInput?: string,
  lastReturn?: DocumentSnapshot,
  limitCount: number = 10
): Promise<ReturnsQueryResult> => {
  if (isSelfHosted) {
    return fetchReturnsPageSelfHosted(
      organizationId,
      searchInput,
      lastReturn,
      limitCount
    );
  }

  let baseQuery = collection(
    firestore,
    'organizations',
    organizationId,
    'returns'
  );

  const conditions = [where('deleted', '==', false)];

  if (searchInput && searchInput.trim() !== '') {
    conditions.push(
      where('keywords', 'array-contains', searchInput.toLowerCase())
    );
  }

  let q = query(baseQuery, ...conditions);
  q = query(q, orderBy('created', 'desc'));

  if (lastReturn) {
    q = query(q, startAfter(lastReturn));
  }

  q = query(q, limit(limitCount));

  const snapshot = await getDocs(q);
  const returns: Return[] = [];

  snapshot.forEach(doc => {
    const data = doc.data();
    returns.push({
      ...(data as Omit<Return, 'id'>),
      id: doc.id,
    });
  });

  return {
    returns,
    lastDoc:
      snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
    hasMore: snapshot.docs.length >= limitCount,
  };
};

/**
 * Subscribe to returns with real-time updates (Firebase snapshot)
 * This is only for reading - all mutations should go through the server API
 */
export const subscribeToReturns = (
  organizationId: string,
  searchInput: string,
  onUpdate: (
    returns: Return[],
    hasMore: boolean,
    lastDoc: QueryDocumentSnapshot | null
  ) => void,
  onError: (error: Error) => void,
  limitCount: number = 50
): Unsubscribe => {
  if (isSelfHosted) {
    return createPollingSubscription(async () => {
      try {
        const result = await fetchReturnsPageSelfHosted(
          organizationId,
          searchInput,
          undefined,
          limitCount
        );
        onUpdate(result.returns, result.hasMore, result.lastDoc);
      } catch (error) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  let baseQuery = collection(
    firestore,
    'organizations',
    organizationId,
    'returns'
  );

  // Build query conditions
  const conditions = [
    where('deleted', '==', false), // Always filter out deleted returns
  ];

  // Add search filter if provided
  if (searchInput && searchInput.trim() !== '') {
    conditions.push(
      where('keywords', 'array-contains', searchInput.toLowerCase())
    );
  }

  let q = query(baseQuery, ...conditions);

  // Add ordering and limit
  q = query(q, orderBy('created', 'desc'), limit(limitCount));

  return onSnapshot(
    q,
    snapshot => {
      const returns: Return[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        returns.push({
          ...(data as Omit<Return, 'id'>),
          id: doc.id,
        });
      });

      const hasMore = snapshot.docs.length >= limitCount;
      const lastDoc =
        snapshot.docs.length > 0
          ? snapshot.docs[snapshot.docs.length - 1]
          : null;

      onUpdate(returns, hasMore, lastDoc);
    },
    onError
  );
};
